import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../App';
import { useTheme } from '../theme';
import { apiUrl } from '../api';

/**
 * Floating contact buttons (phone + WhatsApp) shown only on mobile.
 * Reads numbers from server settings and localStorage as fallback.
 */
export default function MobileContactButtons() {
  const { tokens } = useTheme();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [siteInfo, setSiteInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('siteInfo') || '{}'); } catch { return {}; }
  });
  
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 760;
  
  // Fetch siteInfo from server on mount
  useEffect(() => {
    fetch(apiUrl('/api/settings'))
      .then(res => res.json())
      .then(data => {
        if (data.siteInfo) {
          setSiteInfo(data.siteInfo);
          try { localStorage.setItem('siteInfo', JSON.stringify(data.siteInfo)); } catch {}
        }
      })
      .catch(err => console.error('Failed to fetch settings:', err));
  }, []);
  
  if (!isMobile) return null;
  
  const phone = (siteInfo.phone || '').trim();
  const whatsapp = (siteInfo.whatsapp || '').replace(/[^\d+]/g, '').trim(); // store raw digits with optional leading +
  const phoneHref = phone ? `tel:${phone.replace(/[^\d+]/g,'')}` : undefined;
  const waHref = whatsapp ? `https://wa.me/${whatsapp.replace(/^\+/, '')}` : undefined;

  const baseStyle = {
    width: 54,
    height: 54,
    borderRadius: '50%',
    border: `3px solid ${tokens.inverse}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 11,
    color: tokens.inverse,
    textDecoration: 'none',
    boxShadow: `0 6px 18px ${tokens.shadow}`,
    background: `linear-gradient(145deg, ${tokens.accent}, ${tokens.accent2})`,
  };

  return (
    <div style={{ position: 'fixed', right: 16, bottom: 100, zIndex: 1200, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <a
        href={phoneHref || '#'}
        onClick={(e) => { if (!phoneHref) e.preventDefault(); }}
        style={baseStyle}
        title={phone ? `Sună ${phone}` : 'Setează numărul în modul editare'}
      >
        <svg width="34" height="34" viewBox="0 0 76 76" fill="none">
          <path d="M 50.9225,54.2329C 51.037,54.5029 51.0943,54.7769 51.0943,55.0551C 51.0943,55.4396 50.992,55.7986 50.7875,56.132C 50.583,56.4654 50.2966,56.72 49.9285,56.8959L 48.1981,57.7059C 47.5231,58.0127 46.8471,58.2356 46.1701,58.3747C 45.4931,58.5138 44.809,58.5833 44.1176,58.5833C 43.005,58.5833 41.9056,58.3982 40.8195,58.028C 39.7334,57.6578 38.6944,57.1281 37.7024,56.4388C 36.7104,55.7495 35.7798,54.916 34.9105,53.9384C 34.0412,52.9607 33.263,51.8705 32.5757,50.6678C 32.138,50.0011 31.6778,49.2402 31.1951,48.3852C 30.7861,47.6571 30.3187,46.7848 29.7931,45.7682C 29.2674,44.7517 28.7366,43.6237 28.2008,42.3842C 27.6526,41.1447 27.218,40.0617 26.8968,39.1352C 26.5757,38.2086 26.3272,37.4386 26.1513,36.825C 25.9468,36.1091 25.8057,35.4955 25.7279,34.9841C 25.4457,34.0351 25.2381,33.0912 25.1051,32.1524C 24.9722,31.2136 24.9057,30.2839 24.9057,29.3635C 24.9057,28.1609 25.0264,27.0032 25.2677,25.8905C 25.5091,24.7779 25.8793,23.7429 26.3783,22.7857C 26.8774,21.8285 27.5104,20.9725 28.2775,20.2178C 29.0445,19.463 29.9516,18.8484 30.9988,18.3739L 32.7046,17.5885C 32.9746,17.4739 33.2446,17.4167 33.5146,17.4167C 33.895,17.4167 34.2488,17.522 34.5761,17.7327C 34.9033,17.9433 35.1488,18.2348 35.3124,18.6071L 38.5952,26.0623C 38.7097,26.3323 38.767,26.6023 38.767,26.8723C 38.767,27.2691 38.6647,27.6362 38.4602,27.9737C 38.2557,28.3112 37.9673,28.5638 37.595,28.7319L 34.2202,30.2839C 33.7743,30.4885 33.4327,30.7891 33.1955,31.1859C 32.9582,31.5827 32.8396,32.0102 32.8396,32.4684C 32.8396,32.8161 32.905,33.1433 33.0359,33.4501L 38.5768,46.1026C 38.7936,46.5894 39.0932,46.9474 39.4757,47.1764C 39.8582,47.4055 40.2744,47.5201 40.7244,47.5201C 41.0312,47.5201 41.3441,47.4505 41.6632,47.3114L 45.038,45.759C 45.3039,45.6445 45.5719,45.5872 45.8419,45.5872C 46.2141,45.5872 46.5659,45.6925 46.8973,45.9032C 47.2286,46.1139 47.4781,46.4033 47.6458,46.7715L 50.9225,54.2329 Z" fill="#FFFFFF" fillOpacity="1" strokeWidth="0.2" strokeLinejoin="round"/>
        </svg>
      </a>
      <a
        href={waHref || '#'}
        onClick={(e) => { if (!waHref) e.preventDefault(); }}
        style={{ ...baseStyle, background: 'linear-gradient(145deg,#25D366,#128C7E)' }}
        title={whatsapp ? `Mesaj ${whatsapp}` : 'Setează numărul în modul editare'}
      >
        <svg width="36" height="36" viewBox="0 0 76 76" fill="none">
          <path d="M 47.0915,41.7457C 46.6242,41.4897 44.3263,40.25 43.8957,40.0748C 43.4651,39.8996 43.1507,39.8082 42.8157,40.2737C 42.4802,40.7372 41.527,41.7756 41.238,42.0831C 40.9479,42.3907 40.6689,42.4193 40.2016,42.1627C 39.7343,41.9073 38.2181,41.3462 36.4608,39.6566C 35.0925,38.3418 34.1953,36.7467 33.9349,36.2595C 33.6745,35.773 33.933,35.5244 34.1803,35.2964C 34.4028,35.0907 34.6775,34.7576 34.926,34.4886C 35.1739,34.2195 35.2616,34.0244 35.4318,33.7125C 35.6027,33.4005 35.5337,33.1196 35.4237,32.8773C 35.3131,32.6343 34.4488,30.2557 34.0878,29.2876C 33.7267,28.3201 33.3241,28.4636 33.0463,28.453C 32.7686,28.4425 32.4523,28.3909 32.1347,28.3791C 31.8172,28.3673 31.2971,28.4667 30.8429,28.9278C 30.3886,29.387 29.1154,30.4931 29.0278,32.8363C 28.9402,35.1789 30.5608,37.5073 30.787,37.8329C 31.0131,38.1597 33.8827,43.2383 38.6518,45.3274C 43.4216,47.4152 43.4458,46.7789 44.3214,46.7317C 45.1975,46.6857 47.1822,45.6853 47.6209,44.5886C 48.0596,43.4924 48.095,42.5386 47.9832,42.3354C 47.8713,42.1328 47.5582,42.0011 47.0915,41.7457 Z M 38.3449,53.0363C 35.1907,53.0363 32.2534,52.0899 29.8008,50.4687L 23.833,52.3789L 25.773,46.6124C 23.9138,44.0517 22.8158,40.9056 22.8158,37.5073C 22.8158,28.9433 29.7822,21.977 38.3449,21.977C 46.9082,21.977 53.8745,28.9433 53.8745,37.5073C 53.8745,46.0699 46.9082,53.0363 38.3449,53.0363 Z M 38.3449,18.852C 28.0423,18.852 19.6903,27.2041 19.6903,37.5073C 19.6903,41.0317 20.6689,44.3276 22.3672,47.14L 18.9999,57.148L 29.3286,53.8416C 32.0011,55.3193 35.0745,56.1619 38.3449,56.1619C 48.6474,56.1619 57.0001,47.8092 57.0001,37.5073C 57.0001,27.2041 48.6474,18.852 38.3449,18.852 Z" fill="#FFFFFF" fillOpacity="1" fillRule="evenodd" strokeLinejoin="round"/>
        </svg>
      </a>
      {token && (
        <div style={{ fontSize: 10, textAlign: 'center', color: tokens.textMuted, maxWidth: 70 }}>Editează numerele din modul editare Home</div>
      )}
    </div>
  );
}
